/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 *
 * Outlook task pane: read-mode intake for ORR AIRA.
 *
 * On load this:
 *   1. Confirms the selected item is a read-mode email
 *   2. Loads POWERAPP_BASE_URL from /api/config
 *   3. Converts the Outlook itemId to a REST/Graph id
 *   4. POSTs messageId + mailbox to the middle tier (SSO + Graph + Dataverse)
 *   5. Sets the Power Apps iframe src to baseUrl?recordId=<guid>
 *
 * Email subject/body are never put on the launch URL.
 * Shared-mailbox opens reuse the same Dataverse row (mailbox + Outlook message id).
 */

/* global document, Office, console */

Office.onReady((info) => {
  if (info.host !== Office.HostType.Outlook) {
    return;
  }

  document.getElementById("sideload-msg").style.display = "none";
  document.getElementById("app-body").style.display = "flex";

  const PowerAppIFrame = document.getElementById("PowerAppIFrame");
  const EmailItem = Office.context.mailbox.item;

  /**
   * Hide a status banner by element id.
   * @param {string} bannerId
   */
  function hideBannerById(bannerId) {
    const banner = document.getElementById(bannerId);
    if (banner) {
      banner.style.display = "none";
    }
  }

  /**
   * Wire dismiss buttons that use data-dismiss-target="<banner-id>".
   */
  function setupBannerDismissHandlers() {
    const dismissButtons = document.querySelectorAll("[data-dismiss-target]");
    dismissButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const bannerId = button.getAttribute("data-dismiss-target");
        if (bannerId) {
          hideBannerById(bannerId);
        }
      });
    });
  }

  function hideWarning() {
    hideBannerById("warning-banner");
  }

  /**
   * Show a blocking error and hide the warning banner.
   * @param {string} message
   */
  function showError(message) {
    const banner = document.getElementById("error-banner");
    const msg = document.getElementById("error-banner__message");
    if (banner && msg) {
      hideWarning();
      msg.textContent = message;
      banner.style.display = "flex";
    }
    console.error(message);
  }

  /**
   * Show a non-blocking notice.
   * @param {string} message
   */
  function showWarning(message) {
    const banner = document.getElementById("warning-banner");
    const msg = document.getElementById("warning-banner__message");
    if (banner && msg) {
      msg.textContent = message;
      banner.style.display = "flex";
    }
    console.warn(message);
  }

  /**
   * Toggle the loading banner and optional status text.
   * @param {boolean} isLoading
   * @param {string} [text]
   */
  function setLoading(isLoading, text) {
    const loader = document.getElementById("loading-banner");
    const loaderMsg = document.getElementById("loading-banner__message");
    if (loader) {
      loader.style.display = isLoading ? "flex" : "none";
    }
    if (loaderMsg && text) {
      loaderMsg.textContent = text;
    }
  }

  /**
   * True when the current item is an opened (read) message, not a compose draft.
   * Compose items have no itemId / from in the same way.
   * @param {Office.MessageRead} item
   * @returns {boolean}
   */
  function isMessageReadItem(item) {
    return Boolean(
      item &&
        typeof item.itemId === "string" &&
        item.itemId.length > 0 &&
        item.from !== undefined
    );
  }

  /**
   * Convert the EWS/Outlook itemId to a REST id Graph can use.
   * iOS/Android already return REST ids, so skip convertToRestId there.
   *
   * @param {Office.MessageRead} item
   * @returns {string}
   */
  function getRestMessageId(item) {
    const rawId = item.itemId;
    if (!rawId) {
      throw new Error("The selected item does not have an Outlook item ID.");
    }

    const hostName =
      (Office.context.mailbox.diagnostics &&
        Office.context.mailbox.diagnostics.hostName) ||
      "";
    if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
      return rawId;
    }

    return Office.context.mailbox.convertToRestId(
      rawId,
      Office.MailboxEnums.RestVersion.v2_0
    );
  }

  /**
   * Resolve the shared/delegate mailbox SMTP when the item is from a shared inbox.
   * Returns null for a personal mailbox or when Mailbox 1.8 is unavailable.
   *
   * @returns {Promise<string|null>}
   */
  function getSharedMailboxAsync() {
    return new Promise((resolve) => {
      if (
        !Office.context.mailbox.item.getSharedPropertiesAsync ||
        !Office.context.requirements.isSetSupported("Mailbox", "1.8")
      ) {
        resolve(null);
        return;
      }

      Office.context.mailbox.item.getSharedPropertiesAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
          resolve(
            result.value.targetMailbox ||
              result.value.owner ||
              result.value.delegateMailbox ||
              null
          );
          return;
        }
        resolve(null);
      });
    });
  }

  /**
   * Load public add-in config (Power Apps launch URL).
   * @returns {Promise<{ powerAppBaseUrl: string }>}
   */
  async function loadConfig() {
    try {
      const response = await fetch("/api/config", { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`Config fetch failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      showError(
        "Could not load the add-in configuration from the server. " +
          `(Detail: ${error.message})`
      );
      throw error;
    }
  }

  /**
   * Office SSO token for the middle-tier API (audience = add-in app registration).
   * @returns {Promise<string>}
   */
  async function getMiddleTierToken() {
    return Office.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
    });
  }

  /**
   * POST the selected message to the middle tier to create/refresh the Dataverse row.
   * 401/403 with a claims payload is treated as an MFA challenge for the caller.
   *
   * @param {string} messageId REST/Graph id
   * @param {string|null} mailbox Shared mailbox SMTP, or null for /me
   * @param {string} token Office SSO bearer token
   * @returns {Promise<{ recordId: string, created: boolean }>}
   */
  async function createOrRefreshCaseRequest(messageId, mailbox, token) {
    const response = await fetch("/api/case-requests/from-email", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        messageId,
        mailbox: mailbox || null,
      }),
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch (e) {
      payload = null;
    }

    if (response.status === 401 || response.status === 403) {
      const claims = payload && payload.claims;
      if (claims) {
        const err = new Error("Additional authentication required.");
        err.claims = claims;
        err.status = response.status;
        throw err;
      }
    }

    if (!response.ok) {
      const detail =
        (payload && (payload.message || payload.error)) ||
        `HTTP ${response.status}`;
      const err = new Error(detail);
      err.status = response.status;
      err.correlationId = payload && payload.correlationId;
      throw err;
    }

    if (!payload || !payload.recordId) {
      throw new Error("The server did not return a case request id.");
    }

    return payload;
  }

  /**
   * Append recordId to the Canvas app URL without duplicating "?".
   * @param {string} baseUrl
   * @param {string} recordId
   * @returns {string}
   */
  function buildPowerAppUrl(baseUrl, recordId) {
    const separator = baseUrl.includes("?") ? "&" : "?";
    const params = new URLSearchParams({ recordId });
    return `${baseUrl}${separator}${params.toString()}`;
  }

  /**
   * End-to-end intake: config → Graph id → Dataverse upsert → iframe launch.
   */
  async function runIntake() {
    setupBannerDismissHandlers();
    hideWarning();
    setLoading(true, "Preparing the selected email for AIRA...");

    try {
      if (!isMessageReadItem(EmailItem)) {
        throw new Error(
          "ORR AIRA can only process a selected email in read mode."
        );
      }

      const [config, sharedMailbox] = await Promise.all([
        loadConfig(),
        getSharedMailboxAsync(),
      ]);

      if (!config.powerAppBaseUrl) {
        throw new Error("Power Apps URL is not configured on the server.");
      }

      const messageId = getRestMessageId(EmailItem);
      const mailbox = sharedMailbox || null;

      setLoading(true, "Signing in and saving the email to Dataverse...");

      let token = await getMiddleTierToken();
      let result;
      try {
        result = await createOrRefreshCaseRequest(messageId, mailbox, token);
      } catch (firstErr) {
        // Conditional Access: retry once with the claims challenge from Entra.
        if (firstErr.claims) {
          token = await Office.auth.getAccessToken({
            authChallenge: firstErr.claims,
          });
          result = await createOrRefreshCaseRequest(messageId, mailbox, token);
        } else {
          throw firstErr;
        }
      }

      hideWarning();
      const appUrl = buildPowerAppUrl(config.powerAppBaseUrl, result.recordId);
      PowerAppIFrame.setAttribute("src", appUrl);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      PowerAppIFrame.setAttribute("src", "");
      const correlation =
        error.correlationId ? ` Correlation: ${error.correlationId}.` : "";
      showError(
        "The add-in could not create or open the AIRA case request. " +
          "Please close and reopen this panel, then try again. " +
          `(Detail: ${error.message || error})${correlation}`
      );
    }
  }

  runIntake();
});

/**
 * Kept for webpack/export compatibility with the Office add-in template.
 * Intake runs from Office.onReady, not this export.
 */
export async function run() {
  // Kept for webpack/export compatibility with the Office add-in template.
}
